import { useMemo } from 'react';
import { useMutation } from 'react-query';
import type { MutationOptions } from 'react-query';
import HouseholdService from '../../services/householdService';
import useAbortController from '../useAbortController';

const useDeleteHouseholdMemberMutation = ({
  organizationId,
  propertyId,
  householdId,
  options,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
  options?: MutationOptions,
}) => {
  const householdService = useMemo(() => new HouseholdService(), []);
  const abortControllerOptions = useAbortController();

  // Exactly one of residentId/applicantId identifies the member. A Pending
  // member has no Resident row until move-in, so it can only be addressed on
  // the applicant-scoped route; both are named rather than collapsed into one
  // id so the route can never be picked from the wrong one.
  return useMutation(({ residentId, applicantId } = {}) => {
    // Fail loudly rather than issuing a DELETE against a URL with `undefined` in it.
    // The caller's own guard is silent, so without this a click with neither id would
    // simply evaporate instead of surfacing through onError.
    if (!residentId && !applicantId) {
      return Promise.reject(
        new Error(
          'Deleting a household member needs either a residentId or an applicantId.',
        ),
      );
    }

    return applicantId
      ? householdService.deleteHouseholdApplicant(
          organizationId,
          propertyId,
          householdId,
          applicantId,
          abortControllerOptions,
        )
      : householdService.deleteHouseholdMember(
          organizationId,
          propertyId,
          householdId,
          residentId,
          abortControllerOptions,
        );
  }, options);
};

export default useDeleteHouseholdMemberMutation;
